import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from "react"

import type { FilterOption } from "../../model/types"

interface UseControlledMultiselectProps {
	options: FilterOption[]
	selectedValues: (string | number)[]
	onChange: (selectedValues: (string | number)[]) => void
	disabled: boolean
	label: string
	placeholder?: string
	dataCy: string
}

export const useControlledMultiselect = ({
	options,
	selectedValues,
	onChange,
	disabled,
	label,
	placeholder,
	dataCy,
}: UseControlledMultiselectProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [deferredQuery, setDeferredQuery] = useState("")
	const [, startTransition] = useTransition()
	const containerRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const listboxId = `${dataCy}-listbox`

	const filteredOptions = useMemo(() => {
		const query = deferredQuery.toLowerCase()
		if (!query) return options

		return options
			.filter(option => option.label.toLowerCase().includes(query))
			.sort((a, b) => {
				const indexA = a.label.toLowerCase().indexOf(query)
				const indexB = b.label.toLowerCase().indexOf(query)
				return indexA - indexB
			})
	}, [options, deferredQuery])

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value
			setSearchQuery(value)
			startTransition(() => {
				setDeferredQuery(value)
			})
		},
		[startTransition],
	)

	const toggleDropdown = useCallback(() => {
		if (disabled) return
		setIsOpen(prev => {
			if (prev) {
				setSearchQuery("")
				setDeferredQuery("")
			}
			return !prev
		})
	}, [disabled])

	const handleToggleValue = useCallback(
		(value: string | number) => {
			const updated = selectedValues.includes(value)
				? selectedValues.filter(v => v !== value)
				: [...selectedValues, value]
			onChange(updated)
		},
		[selectedValues, onChange],
	)

	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				event.target instanceof Node &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false)
				setSearchQuery("")
				setDeferredQuery("")
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () =>
			document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (event.key !== "Escape") {
			return
		}

		event.stopPropagation()
		setIsOpen(false)
		setSearchQuery("")
		setDeferredQuery("")
		triggerRef.current?.focus()
	}, [])

	const triggerLabel = useMemo(() => {
		if (selectedValues.length === 0) {
			return placeholder ?? label
		}
		if (selectedValues.length > 2) {
			return `${selectedValues.length} selected`
		}
		return selectedValues
			.map(v => options.find(o => o.value === v)?.label)
			.filter(Boolean)
			.join(", ")
	}, [selectedValues, options, label, placeholder])

	return {
		isOpen,
		searchQuery,
		deferredQuery,
		filteredOptions,
		containerRef,
		searchInputRef,
		triggerRef,
		listboxId,
		triggerLabel,
		toggleDropdown,
		handleSearchChange,
		handleKeyDown,
		handleToggleValue,
		selectedValues,
	}
}
